/**
 * The one home for the renter's short-lived credential (1NOW-1557).
 *
 * Renters deliberately do NOT have accounts on Template sites. The bearer token
 * exists only so the implicit login inside the booking flow can complete or edit
 * that booking — a transient need. It was previously written to `localStorage`,
 * which persisted it on the renter's device indefinitely after the flow ended:
 * a transient credential in permanent storage. Because every operator's renter
 * site runs from one shared build, any XSS on any of those domains (template
 * bug, compromised package, operator-pasted third-party script) could read it.
 *
 * It now lives in `sessionStorage`, so it dies when the tab closes and is scoped
 * per-tab as well as per-origin. The API is synchronous and identical, so the
 * axios interceptor keeps its shape.
 *
 * Deliberately NOT encrypted. Client-side encryption cannot mitigate XSS: the
 * key must live in the same JS context the injected script runs in, so it could
 * decrypt the value — or simply call our APIs through our own axios client,
 * which attaches the token for it. That would be obfuscation, not security.
 * (The FMS's AES-GCM layer is not a model to copy — it wraps only the profile
 * blob, with a hardcoded key in the bundle, while its own bearer token sits in
 * plain localStorage.)
 */

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "user";

const SESSION_KEYS = [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY] as const;

/**
 * Storage access can throw outright — Safari private mode and some embedded
 * webviews raise on `sessionStorage` access rather than returning null. The
 * booking flow must not hard-fail in that case, so every access is guarded and
 * degrades to "no stored session" (the flow re-authenticates).
 */
const readKey = (store: Storage | undefined, key: string): string | null => {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeKey = (store: Storage | undefined, key: string, value: string) => {
  try {
    store?.setItem(key, value);
  } catch {
    /* storage unavailable — the flow re-auths rather than breaking */
  }
};

const dropKey = (store: Storage | undefined, key: string) => {
  try {
    store?.removeItem(key);
  } catch {
    /* nothing to do */
  }
};

const session = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.sessionStorage;

const local = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.localStorage;

/** Shape the login/customer endpoints return. */
export interface RenterSessionPayload {
  access_token?: string;
  refresh_token?: string;
  [key: string]: any;
}

export const getAccessToken = (): string | null =>
  readKey(session(), ACCESS_TOKEN_KEY);

/**
 * Persist the credential returned by an implicit login / customer lookup.
 * The whole response doubles as the `user` object, matching prior behaviour.
 */
export const setRenterSession = (payload: RenterSessionPayload): void => {
  const store = session();
  if (payload?.access_token) {
    writeKey(store, ACCESS_TOKEN_KEY, payload.access_token);
  }
  if (payload?.refresh_token) {
    writeKey(store, REFRESH_TOKEN_KEY, payload.refresh_token);
  }
  writeKey(store, USER_KEY, JSON.stringify(payload));
  // A fresh session supersedes anything a previous build left on the device.
  purgeLegacyPersistedSession();
};

/** Drop the credential. Clears legacy localStorage copies too. */
export const clearRenterSession = (): void => {
  const store = session();
  SESSION_KEYS.forEach((key) => dropKey(store, key));
  purgeLegacyPersistedSession();
};

/**
 * Read the stored renter profile. A value that won't parse is cleared rather
 * than left in place — otherwise a corrupt blob keeps failing on every mount
 * with nothing but a console error, which is the stuck state we want to avoid.
 */
export const getStoredRenterUser = (): any | null => {
  const raw = readKey(session(), USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    clearRenterSession();
    return null;
  }
};

/**
 * Remove credentials written by builds that used `localStorage`.
 *
 * This runs on app start, so a returning renter who still has a token sitting
 * on their device from before this change gets it deleted rather than carried
 * forward. Without this the old value would linger indefinitely — exactly the
 * exposure the ticket is closing — and the axios interceptor no longer reads it,
 * so keeping it would be pure risk with no function.
 */
export const purgeLegacyPersistedSession = (): void => {
  const store = local();
  SESSION_KEYS.forEach((key) => dropKey(store, key));
};
